import {
  ManagedLeadDetail,
  ManagedLeadField,
  ManagedLeadProductPath,
} from '../../services/api/managed-leads/managedLeads';
import {
  canonicalManagedLeadAmount,
  managedLeadCurrencyToCanonical,
  managedLeadCurrencyToDisplay,
} from './managedLeadCurrency';
import { getManagedLeadFieldValue } from './managedLeadFormatting';

/**
 * Correcting the figures a customer stated at acquisition, on the client side:
 * which of them a product carries, when the pencil may be offered at all, what
 * the modal opens on, what counts as a change and what travels on the wire.
 *
 * It is the sibling of `managedLeadAdvancementData` and deliberately not part
 * of it. The advancement is stage-owned and server-driven - the policy sends
 * the descriptors and the backoffice renders whatever arrives. A correction is
 * a fixed set of acquisition figures no stage publishes, so the set is declared
 * here, symmetrically with the set the server declares.
 */

/** What the operator is editing, keyed by semantic name, in the input's shape. */
export type ManagedLeadRequestValues = Record<string, string>;

/** One message per rejected semantic key, ready to sit under its input. */
export type ManagedLeadRequestValuesErrors = Record<string, string>;

export const MANAGED_LEAD_REQUEST_VALUES_TITLE = 'Correggi i valori richiesti';

export const MANAGED_LEAD_REQUEST_VALUES_EDIT_LABEL =
  'Modifica valori richiesti';

export const MANAGED_LEAD_REQUEST_VALUE_INVALID_AMOUNT_MESSAGE =
  'Inserisci un importo maggiore di zero, con al massimo due decimali.';

export const MANAGED_LEAD_REQUEST_VALUE_NOT_EMPTIABLE_MESSAGE =
  'Questo valore non può essere svuotato.';

/**
 * The mensilità a CdQ pipeline recognises. It is closed, which is why the
 * figure is chosen and never typed.
 */
const MONTHLY_INCOME_FREQUENCIES = ['12', '13', '14'];

const INVALID_CHOICE_MESSAGE = 'Scegli uno dei valori proposti.';

/** A monetary correction carries at most two decimals, as the server asks. */
const MAX_AMOUNT_DECIMALS = 2;

/**
 * The detail fields a correction reads from. `monthlyIncomeFrequency` is
 * optional because only CdQ carries it: Prestito neither collects nor stores
 * the figure, and a product that does not declare it never looks for it.
 */
type ManagedLeadRequestValueSource =
  | 'amount'
  | 'income'
  | 'monthlyIncomeFrequency';

type ManagedLeadRequestValueFields = Partial<
  Record<ManagedLeadRequestValueSource, ManagedLeadField<string | number>>
>;

/**
 * How one figure is edited: the shape a stored value takes in its input, the
 * shape an edited one travels in, what two texts have to share to be the same
 * figure, why a non-empty value is unacceptable, and - for a figure with a
 * closed vocabulary - what it may be chosen from.
 */
interface ManagedLeadValueEditor {
  toEditor: (stored: string) => string;
  toCanonical: (edited: string) => string;
  comparable: (text: string) => string;
  /** The message the canonical value fails on, or `null` when it holds. */
  reject: (canonical: string) => string | null;
  /**
   * The values on offer for a figure that is chosen rather than typed, given
   * what HubSpot holds for it. A figure that is typed has none, and that is
   * what tells the modal which input to render.
   */
  options?: (stored: string) => string[];
}

const CURRENCY: ManagedLeadValueEditor = {
  toEditor: managedLeadCurrencyToDisplay,
  toCanonical: managedLeadCurrencyToCanonical,
  // An amount is compared canonically, so `25.000` and `25000,00` are one
  // figure. A text that is not an amount is compared as it stands: what the
  // backoffice cannot read, it neither rewrites nor reports as changed.
  comparable: (text) => canonicalManagedLeadAmount(text) ?? text.trim(),
  reject: (canonical) => {
    const amount = canonicalManagedLeadAmount(canonical);
    const decimals = canonical.split('.')[1]?.length ?? 0;
    return amount === null ||
      decimals > MAX_AMOUNT_DECIMALS ||
      Number(amount) <= 0
      ? MANAGED_LEAD_REQUEST_VALUE_INVALID_AMOUNT_MESSAGE
      : null;
  },
};

/**
 * One correctable figure. `key` is the semantic name of the public contract -
 * the HubSpot property behind it is private to the backend and never reaches
 * here - and `source` names the detail field that already carries its value, so
 * opening the modal costs no read of its own.
 */
interface ManagedLeadCorrectableValue {
  key: string;
  /** How the operator hears the figure named, in the modal and in a rejection. */
  label: string;
  source: ManagedLeadRequestValueSource;
  editor: ManagedLeadValueEditor;
}

const REQUESTED_AMOUNT: ManagedLeadCorrectableValue = {
  key: 'requestedAmount',
  label: 'Importo richiesto',
  source: 'amount',
  editor: CURRENCY,
};

/**
 * An amount like the requested one, and edited by the same rules: the figure is
 * money the customer stated, so it is grouped, canonicalised and refused
 * exactly as the amount is.
 *
 * The label is not a wording choice either. The figure is monthly - the funnel
 * fills it from the customer's monthly income - and while it was only read the
 * ambiguity cost little. In a form an operator types into, an annual figure
 * produces a value ten times too large that looks entirely plausible and that
 * no validation can reject, so the field says what it wants.
 */
const MONTHLY_INCOME: ManagedLeadCorrectableValue = {
  key: 'monthlyIncome',
  label: 'Reddito mensile',
  source: 'income',
  editor: CURRENCY,
};

/**
 * A closed vocabulary, so the figure is chosen and never typed: an operator
 * cannot enter a mensilità the pipeline does not recognise.
 *
 * The case worth care is the lead HubSpot already holds outside that
 * vocabulary. It is a data problem the backoffice did not create, so the value
 * is offered as it stands and is what the field opens on: resetting it to a
 * valid mensilità would rewrite data nobody asked to change, and blanking it
 * would hide it. The offer is keyed to what is stored and not to what is
 * selected, so it stays on the menu once the operator has moved away from it:
 * choosing it again is how they leave the figure alone without reopening.
 */
const choiceOver = (vocabulary: string[]): ManagedLeadValueEditor => ({
  toEditor: (stored) => stored,
  toCanonical: (edited) => edited.trim(),
  comparable: (text) => text.trim(),
  // No option on offer can fail this, and the one value outside the vocabulary
  // is never a delta. It states what the field accepts anyway, so the rule the
  // server enforces is written down on this side too.
  reject: (canonical) =>
    vocabulary.includes(canonical) ? null : INVALID_CHOICE_MESSAGE,
  options: (stored) =>
    stored && !vocabulary.includes(stored)
      ? [stored, ...vocabulary]
      : vocabulary,
});

const MONTHLY_INCOME_FREQUENCY: ManagedLeadCorrectableValue = {
  key: 'monthlyIncomeFrequency',
  label: 'Mensilità',
  source: 'monthlyIncomeFrequency',
  editor: choiceOver(MONTHLY_INCOME_FREQUENCIES),
};

/**
 * Every figure a product declares correctable, in the order the payload states
 * them. The set mirrors the one the server declares: a key it does not declare
 * is rejected rather than ignored, and a declared key that does not arrive is
 * rejected too, so this list is what makes a correction well-formed at all.
 * Mensilità is CdQ's alone, because Prestito neither collects nor stores it.
 */
const CORRECTABLE_VALUES: Record<
  ManagedLeadProductPath,
  ManagedLeadCorrectableValue[]
> = {
  prestiti: [REQUESTED_AMOUNT, MONTHLY_INCOME],
  'cessioni-del-quinto': [
    REQUESTED_AMOUNT,
    MONTHLY_INCOME,
    MONTHLY_INCOME_FREQUENCY,
  ],
};

/**
 * Every figure a product offers, in the order the modal renders them and the
 * payload states them.
 */
export const managedLeadCorrectableValues = (product: ManagedLeadProductPath) =>
  CORRECTABLE_VALUES[product] ?? [];

/**
 * How the operator hears each figure the modal shows. A rejection names the
 * field with the words the input carries, so there is nothing to map twice.
 */
export const managedLeadCorrectableValueLabels = (
  product: ManagedLeadProductPath
) =>
  managedLeadCorrectableValues(product).reduce<Record<string, string>>(
    (labels, value) => ({ ...labels, [value.key]: value.label }),
    {}
  );

/**
 * Whether the values section may offer its pencil. This is stage policy, and it
 * is deliberately **not** `canUpdateManagedLead`, which answers a different
 * question: Da assegnare and Da controllare freeze the commercial process and
 * not the customer's data, so they refuse an advancement and accept a
 * correction. What closes the pencil is a pratica that is closed - Drop,
 * Liquidata - a stage the backoffice does not recognise, and a Drop awaiting
 * review, which suspends every write until an admin has decided.
 *
 * The server enforces the same rule and stays authoritative; the pencil only
 * mirrors it, and mirrors it fail-closed: a stage carrying any alert is one the
 * backoffice already reads as unsupported and read-only.
 */
export const canCorrectManagedLeadRequestValues = (detail: ManagedLeadDetail) =>
  !detail.pendingDrop &&
  !detail.stage.alert &&
  detail.stage.category === 'in_progress';

/**
 * The text HubSpot holds for one figure, read from the detail already on the
 * page. A figure it holds nothing for reads empty, which is the one case the
 * server lets a correction leave empty.
 */
const storedText = (
  detail: ManagedLeadDetail,
  value: ManagedLeadCorrectableValue
) => {
  const field = (detail.fields as ManagedLeadRequestValueFields)[value.source];
  const stored = field ? getManagedLeadFieldValue(field) : null;
  return stored === null || stored === undefined ? '' : String(stored).trim();
};

/**
 * What the modal opens on: what HubSpot holds, and nothing else. It is read
 * from the detail response the page already has, so the pencil adds no request.
 */
export const initialManagedLeadRequestValues = (
  product: ManagedLeadProductPath,
  detail: ManagedLeadDetail
): ManagedLeadRequestValues =>
  managedLeadCorrectableValues(product).reduce<ManagedLeadRequestValues>(
    (values, value) => ({
      ...values,
      [value.key]: value.editor.toEditor(storedText(detail, value)),
    }),
    {}
  );

/**
 * The semantic keys the operator actually corrected. It drives the decision to
 * send at all: an empty delta is not a failed save, it is nothing to save, and
 * announcing it as a correction would claim a change that never happened.
 */
export const managedLeadRequestValuesDelta = (
  product: ManagedLeadProductPath,
  detail: ManagedLeadDetail,
  values: ManagedLeadRequestValues
): string[] =>
  managedLeadCorrectableValues(product)
    .filter(
      (value) =>
        value.editor.comparable(
          value.editor.toCanonical(values[value.key] ?? '')
        ) !== value.editor.comparable(storedText(detail, value))
    )
    .map((value) => value.key);

/**
 * Whether anything on screen differs from what the modal opened on. It is what
 * Salva is enabled by, and it is deliberately coarser than the delta: two texts
 * that stand for the same figure still count as an edit here, so the operator
 * can press Salva and be told plainly that there was nothing to save.
 */
export const hasManagedLeadRequestValuesEdits = (
  initial: ManagedLeadRequestValues,
  values: ManagedLeadRequestValues
) => Object.keys(initial).some((key) => (values[key] ?? '') !== initial[key]);

/**
 * Mirrors the constraints the server applies, so the operator is told which
 * figure to fix before the round trip. Like the server, it judges the delta
 * alone: a stored figure nobody touched is not the operator's to answer for.
 */
export const validateManagedLeadRequestValues = (
  product: ManagedLeadProductPath,
  detail: ManagedLeadDetail,
  values: ManagedLeadRequestValues
): ManagedLeadRequestValuesErrors => {
  const changed = new Set(
    managedLeadRequestValuesDelta(product, detail, values)
  );

  return managedLeadCorrectableValues(product)
    .filter((value) => changed.has(value.key))
    .reduce<ManagedLeadRequestValuesErrors>((errors, value) => {
      const canonical = value.editor.toCanonical(values[value.key] ?? '');
      // A correction repairs a value and does not delete one: a figure HubSpot
      // holds cannot be blanked, while one it has never held may stay empty
      // rather than push the operator into inventing a plausible figure.
      const message = canonical
        ? value.editor.reject(canonical)
        : storedText(detail, value) &&
          MANAGED_LEAD_REQUEST_VALUE_NOT_EMPTIABLE_MESSAGE;

      return message ? { ...errors, [value.key]: message } : errors;
    }, {});
};

/**
 * The `values` object of the correction payload: every figure the product
 * declares, always present, because the server refuses a payload that is not
 * symmetric with the set it offers. A figure the operator left alone travels
 * exactly as it was read, so the server finds no delta for it and leaves the
 * property alone - which is how a value HubSpot holds outside its vocabulary
 * survives a correction of the figures beside it.
 */
export const managedLeadRequestValuesPayload = (
  product: ManagedLeadProductPath,
  values: ManagedLeadRequestValues
): Record<string, string> =>
  managedLeadCorrectableValues(product).reduce<Record<string, string>>(
    (payload, value) => ({
      ...payload,
      [value.key]: value.editor.toCanonical(values[value.key] ?? ''),
    }),
    {}
  );
